import React from 'react';
import { TextField } from '../components/FarChat/ChatFeedbackOverlays';
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, Paper, Stack, Typography } from '@mui/material';
import type { DavisBaconConstructionType, EstimateCapabilities, EstimateDraft, EstimateLoadData, EstimateReadiness, PriceSearchPrefill, PriceSearchPrefillJob, SourceMatchReviewDecision } from '../api/constructionPriceEstimatorFunctions';
import ConstructionLocationPicker from '../components/ConstructionLocationPicker';
import TimingRequirements from './TimingRequirements';
import ApproximateCostSettings, { type LaborPlanControls } from './ApproximateCostSettings';
import { blockerText } from './ConstructionPriceSupportingEvidence';
import { SavedWorkActionRow } from '../components/SavedWorkActionRow';

export const ConstructionPriceLoadFailure: React.FC<{
  error: string | null; onRetry: () => void;
}> = ({ error, onRetry }) => <Stack spacing={1}>
  <Alert severity="error">{error ?? 'The saved estimate could not be loaded.'}</Alert>
  <Button onClick={onRetry}>Retry loading estimate</Button>
</Stack>;

interface ExternalFieldProps {
  id: string;
  label: React.ReactNode;
  children: React.ReactNode;
  selectCaption?: boolean;
  accessibleContext?: string;
  sx?: Record<string, unknown>;
  required?: boolean;
}

const visuallyHiddenSx = {
  position: 'absolute',
  width: 1,
  height: 1,
  p: 0,
  m: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
} as const;

export const ExternalField: React.FC<ExternalFieldProps> = ({ id, label, children, selectCaption = false, accessibleContext, sx, required = false }) => (
  <Box sx={{ minWidth: 0, ...sx }}>
    <Box
      component={selectCaption ? 'span' : 'label'}
      {...(selectCaption ? { id: `${id}-caption` } : { htmlFor: id })}
      sx={{ display: 'block', mb: 0.5, color: 'text.primary', fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.35 }}
    >
      {label}
      {required && <Box component="span" aria-hidden="true" sx={{ ml: 0.5 }}>*</Box>}
      {selectCaption && accessibleContext && <Box component="span" sx={visuallyHiddenSx}>{accessibleContext}</Box>}
    </Box>
    {children}
  </Box>
);

interface ConstructionPriceContractDetailsProps {
  load: EstimateLoadData | null;
  draft: EstimateDraft;
  capabilities: EstimateCapabilities | undefined;
  blockers: EstimateReadiness['blockers'];
  canEdit: boolean;
  preparingPrefill: boolean;
  fillContractDetailsAvailable: boolean;
  showPrefillEntry: boolean;
  useSavedContractDetails: boolean;
  setUseSavedContractDetails: React.Dispatch<React.SetStateAction<boolean>>;
  prefillJob: PriceSearchPrefillJob | null;
  cancellingPrefill: boolean;
  sourceMatchDecisions: Record<string, SourceMatchReviewDecision['decision']>;
  setSourceMatchDecisions: React.Dispatch<React.SetStateAction<Record<string, SourceMatchReviewDecision['decision']>>>;
  reviewingSourceMatches: boolean;
  sourceSearchBusy: boolean;
  fillContractDetails: () => void;
  cancelPriceSearchDetails: () => void;
  saveSourceMatchReview: () => void;
  editDraft: (update: (current: EstimateDraft) => EstimateDraft) => void;
  updateLocation: (key: string, value: string) => void;
  autosaveOnBlur: () => void;
  flowId: string;
  laborPlan: LaborPlanControls;
  onIncompleteComponentChange: (incomplete: boolean) => void;
  onSettingsChange: (value: EstimateDraft['approximate_cost_plan_settings']) => void;
}

export const ConstructionPriceContractDetails: React.FC<ConstructionPriceContractDetailsProps> = ({
  load, draft, capabilities, blockers, canEdit, preparingPrefill,
  fillContractDetailsAvailable, showPrefillEntry, useSavedContractDetails,
  setUseSavedContractDetails, prefillJob, cancellingPrefill,
  sourceMatchDecisions, setSourceMatchDecisions, reviewingSourceMatches,
  sourceSearchBusy, fillContractDetails, cancelPriceSearchDetails,
  saveSourceMatchReview, editDraft, updateLocation, autosaveOnBlur,
  flowId, laborPlan, onIncompleteComponentChange, onSettingsChange,
}) => {
  const prefill = load?.prefill ?? null;
  const sourceMatchProposal = load?.source_match_proposal ?? null;
  return (
      <Paper component="section" variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }} aria-labelledby="contract-details-heading" onBlur={autosaveOnBlur}>
        <Typography data-tour-id="price-contract-heading" id="contract-details-heading" variant="h5" component="h2" gutterBottom>Contract Details</Typography>
          <Stack data-tour-id="price-contract-fill" alignItems="center" spacing={0} sx={{ mb: 0.5, '& .MuiFormControlLabel-root': { m: 0, minHeight: 44 } }}>
            <SavedWorkActionRow
              action={
                <Button variant="contained" disabled={!canEdit || preparingPrefill || !fillContractDetailsAvailable} onClick={() => void fillContractDetails()}>
                  {preparingPrefill ? 'Filling contract details' : 'Fill contract details'}
                </Button>
              }
              savedWork={
                <FormControlLabel
                  control={<Checkbox inputProps={{ id: 'use-saved-contract-details', name: 'use-saved-contract-details' }} checked={useSavedContractDetails} disabled={!canEdit || preparingPrefill || !fillContractDetailsAvailable || !load?.draft} onChange={(event) => setUseSavedContractDetails(event.target.checked)} />}
                  label="Use saved work"
                />
              }
            />
          </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {showPrefillEntry
            ? (load?.standalone_mode ? 'Fill editable contract details from the saved project scope before searching price sources.' : 'Fill editable contract details from saved workflow context before searching federal awards.')
            : (load?.standalone_mode ? 'The saved project scope helps fill Contract Details.' : 'Saved workflow context is used automatically to match federal awards.')}
        </Typography>
        {preparingPrefill ? (
          <Stack alignItems="center" spacing={1.25} role="status" aria-label="Filling contract details" aria-live="polite" aria-busy sx={{ py: 4 }}>
            <CircularProgress size={28} aria-label="Filling contract details" />
            <Typography>Filling contract details from {load?.standalone_mode ? 'the saved project scope' : 'saved construction characteristics'}…</Typography>
            {canEdit && prefillJob && <Button variant="text" disabled={cancellingPrefill} onClick={() => void cancelPriceSearchDetails()}>
              {cancellingPrefill ? 'Cancelling…' : 'Cancel filling'}
            </Button>}
          </Stack>
        ) : showPrefillEntry ? (
          <Stack spacing={1.5} alignItems="center" role={preparingPrefill ? undefined : 'status'} aria-live="polite" aria-busy={preparingPrefill}>
            {!preparingPrefill && (
              <>
                <Typography>{load?.standalone_mode ? 'Uses the saved project scope to propose editable contract details.' : 'Uses the saved Construction Characteristics from this workflow to propose editable contract details.'} It does not search price sources or create a price.</Typography>
                {!canEdit && <Typography variant="body2" color="text.secondary">Only the workflow owner can fill contract details.</Typography>}
              </>
            )}
          </Stack>
        ) : <>
        {prefill && (
          <Stack spacing={1} sx={{ mb: 2 }}>
            {prefill.status === 'stale' && <Alert severity="warning">Construction Characteristics changed; review and fill contract details again before calculating.</Alert>}
            {prefill.outcome === 'no_usable_details' && <Alert severity="warning">No usable contract details were found. Enter the missing project facts before calculating.</Alert>}
            {(prefill.outcome !== 'no_usable_details') && (
              <Alert severity="info">
                <Typography fontWeight={700}>Proposed from saved {load?.standalone_mode ? 'project scope' : 'Construction Characteristics'}</Typography>
                <Typography variant="body2">These fields are editable. The proposal does not search price sources or create a price.</Typography>
              </Alert>
            )}
          </Stack>
        )}
        {sourceMatchProposal && sourceMatchProposal.candidates.length > 0 && (
          <Paper component="section" variant="outlined" sx={{ p: 2, mb: 2 }} aria-labelledby="review-source-matches-heading">
            <Typography id="review-source-matches-heading" variant="h6" component="h3">Review technology matches</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Confirm or decline each proposed building-technology match. This changes the next technology-detail search only; it does not change prior award totals.</Typography>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              {sourceMatchProposal.candidates.map((candidate) => {
                const decision = sourceMatchDecisions[candidate.proposal_id];
                return (
                  <Box key={candidate.proposal_id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Typography fontWeight={700}>{candidate.review_label}</Typography>
                    {(candidate.scope_label || candidate.measurement_label) && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{[candidate.scope_label, candidate.measurement_label].filter(Boolean).join(' · ')}</Typography>}
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button size="small" variant={decision === 'accept' ? 'contained' : 'outlined'} disabled={!canEdit || reviewingSourceMatches || sourceSearchBusy} onClick={() => setSourceMatchDecisions((current) => ({ ...current, [candidate.proposal_id]: 'accept' }))}>Accept</Button>
                      <Button size="small" color="inherit" variant={decision === 'decline' ? 'contained' : 'outlined'} disabled={!canEdit || reviewingSourceMatches || sourceSearchBusy} onClick={() => setSourceMatchDecisions((current) => ({ ...current, [candidate.proposal_id]: 'decline' }))}>Decline</Button>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
            <Stack alignItems="center" sx={{ mt: 1.5 }}>
              <Button variant="contained" disabled={!canEdit || reviewingSourceMatches || sourceSearchBusy || sourceMatchProposal.candidates.some((candidate) => !sourceMatchDecisions[candidate.proposal_id])} onClick={() => void saveSourceMatchReview()}>
                {reviewingSourceMatches ? 'Saving matches…' : 'Save technology matches'}
              </Button>
            </Stack>
          </Paper>
        )}
        <ConstructionPriceContractFields draft={draft} canEdit={canEdit} preparingPrefill={preparingPrefill} sourceSearchBusy={sourceSearchBusy} capabilities={capabilities} prefill={prefill} blockers={blockers} editDraft={editDraft} updateLocation={updateLocation} autosaveOnBlur={autosaveOnBlur} load={load} flowId={flowId} laborPlan={laborPlan} onIncompleteComponentChange={onIncompleteComponentChange} onSettingsChange={onSettingsChange} />
        </>}
        {(showPrefillEntry || preparingPrefill) && <Stack data-tour-id="price-labor-plan" alignItems="center" spacing={0} sx={{ mt: 1, '& .MuiFormControlLabel-root': { m: 0, minHeight: 44 } }}>
          <Typography variant="h6" component="h3">Labor plan</Typography>
          <Typography variant="body2" color="text.secondary">Complete the project details, location, and required component fields before filling the labor plan.</Typography>
          <SavedWorkActionRow
            action={<Button variant="contained" disabled={!canEdit || preparingPrefill || sourceSearchBusy || laborPlan.prerequisitesReady !== true || laborPlan.busy || laborPlan.applying} onClick={laborPlan.onFill}>Fill labor plan</Button>}
            savedWork={<FormControlLabel control={<Checkbox id="use-saved-labor-plan" checked={Boolean(laborPlan.useSavedWork)} disabled={!canEdit || preparingPrefill || sourceSearchBusy || laborPlan.prerequisitesReady !== true || laborPlan.busy || laborPlan.applying} onChange={(event) => laborPlan.onUseSavedWorkChange?.(event.target.checked)} />} label="Use saved work" />}
          />
        </Stack>}
      </Paper>

  );
};

export const ConstructionPriceDescription: React.FC<{
  value: string;
  canEdit: boolean;
  onChange: (value: string) => void;
  onBlur: () => void;
}> = ({ value, canEdit, onChange, onBlur }) => (
  <Paper component="section" variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, mb: 2 }} aria-labelledby="description-heading">
    <Typography id="description-heading" variant="h5" component="h2" gutterBottom>Description</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
      Paste a project scope or SOW to help fill Contract Details. You can also enter Contract Details manually.
    </Typography>
    <TextField inputProps={{ 'aria-label': 'Description' }} value={value}
      onChange={(event) => onChange(event.target.value)} onBlur={onBlur}
      disabled={!canEdit} multiline minRows={6} fullWidth />
  </Paper>
);

export function contractPrefillPresentation(
  load: EstimateLoadData | null,
  projectBriefText: string,
): {
  prefillAvailable: boolean;
  fillContractDetailsAvailable: boolean;
  showPrefillEntry: boolean;
} {
  const prefillAvailable = load?.capabilities?.price_search_prefill_available === true;
  const fillContractDetailsAvailable = load?.standalone_mode
    ? projectBriefText.trim().length > 0 || load.standalone_scope_document?.extraction_status === 'ready' : prefillAvailable;
  const showPrefillEntry = !load?.standalone_mode
    && load?.draft === null
    && prefillAvailable
    && !['proposed', 'prepared', 'no_usable_details'].includes(String(load?.prefill?.outcome ?? ''));
  return { prefillAvailable, fillContractDetailsAvailable, showPrefillEntry };
}

export const ExternalTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => (
  <Box
    component="textarea"
    {...props}
    sx={{
      boxSizing: 'border-box',
      width: '100%',
      resize: 'vertical',
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      px: 1.5,
      py: 1.25,
      color: 'text.primary',
      bgcolor: 'background.paper',
      font: 'inherit',
      lineHeight: 1.5,
      '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 1 },
      '&:disabled': { color: 'text.disabled', bgcolor: 'action.disabledBackground', cursor: 'not-allowed' },
    }}
  />
);

// Contract Details intentionally uses only three equal columns or a full row.
// Keeping this shared grid at the stage boundary prevents individual controls
// from acquiring ad-hoc half/two-third widths as the form evolves.
const contractDetailsGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(3, minmax(0, 1fr))' },
  gap: 2,
  mb: 2,
} as const;
const contractDetailsFullWidthSx = { gridColumn: { xs: '1 / -1', md: '1 / -1' }, minWidth: 0 } as const;

interface ConstructionPriceContractFieldsProps {
  draft: EstimateDraft;
  canEdit: boolean;
  preparingPrefill: boolean;
  sourceSearchBusy: boolean;
  capabilities: EstimateCapabilities | undefined;
  prefill: PriceSearchPrefill | null;
  blockers: EstimateReadiness['blockers'];
  editDraft: (update: (current: EstimateDraft) => EstimateDraft) => void;
  updateLocation: (key: string, value: string) => void;
  autosaveOnBlur: () => void;
  load: EstimateLoadData | null;
  flowId: string;
  laborPlan: LaborPlanControls;
  onIncompleteComponentChange: (incomplete: boolean) => void;
  onSettingsChange: (value: EstimateDraft['approximate_cost_plan_settings']) => void;
}

export const ConstructionPriceContractFields: React.FC<ConstructionPriceContractFieldsProps> = ({
  draft, canEdit, preparingPrefill, sourceSearchBusy, capabilities, prefill,
  blockers, editDraft, updateLocation, autosaveOnBlur, load, flowId,
  laborPlan, onIncompleteComponentChange, onSettingsChange,
}) => (
        <Box component="fieldset" disabled={preparingPrefill} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }} aria-busy={preparingPrefill}>
        <Box data-tour-id="price-details" sx={{ ...contractDetailsGridSx, position: 'relative' }} data-testid="contract-details-grid">
        <span data-tour-id="price-details-anchor" aria-hidden="true" style={{ position: 'absolute', top: 0, left: '50%', width: 1, height: 1 }} />
        <Box sx={contractDetailsFullWidthSx}>
          <ConstructionLocationPicker
            location={draft.project_location}
            onLocationChange={(location) => editDraft((current) => ({ ...current, project_location: location }))}
            onBlur={autosaveOnBlur}
            canEdit={canEdit}
          />
        </Box>
        <ExternalField id="price-city" label="City"><TextField id="price-city" inputProps={{ 'aria-label': 'City' }} value={draft.project_location.city ?? ''} onChange={(event) => updateLocation('city', event.target.value)} disabled={!canEdit} fullWidth /></ExternalField>
        <ExternalField id="price-postal-code" label="ZIP code"><TextField id="price-postal-code" inputProps={{ 'aria-label': 'ZIP code' }} value={draft.project_location.postal_code ?? ''} onChange={(event) => updateLocation('postal_code', event.target.value)} disabled={!canEdit} fullWidth /></ExternalField>
        <ExternalField id="price-estimate-date" label="Pricing date" required><TextField id="price-estimate-date" inputProps={{ 'aria-label': 'Pricing date', 'aria-required': true }} error={!draft.estimate_date.trim()} helperText="Used as the as-of date for price research and wage determinations." type="date" value={draft.estimate_date} onChange={(event) => editDraft((current) => ({ ...current, estimate_date: event.target.value }))} disabled={!canEdit} fullWidth /></ExternalField>
        <ExternalField id="price-construction-type" label="Project type" required><TextField id="price-construction-type" inputProps={{ 'aria-label': 'Project type', 'aria-required': true }} error={!draft.construction_type.trim()} helperText="For example: renovation, new construction, or repair." value={draft.construction_type} onChange={(event) => editDraft((current) => ({ ...current, construction_type: event.target.value }))} disabled={!canEdit} fullWidth /></ExternalField>
        <ExternalField id="wage-determination-construction-type" label="Construction Type" required selectCaption accessibleContext="Select one confirmed construction type">
            <TextField
              id="wage-determination-construction-type-select"
              required
              select
              value={draft.davis_bacon_construction_type ?? ''}
              onChange={(event) => editDraft((current) => ({ ...current, davis_bacon_construction_type: (event.target.value || null) as DavisBaconConstructionType | null }))}
              SelectProps={{ SelectDisplayProps: { 'aria-labelledby': 'wage-determination-construction-type-caption', 'aria-required': true } }}
              inputProps={{
                id: 'wage-determination-construction-type-input',
                name: 'wage-determination-construction-type',
                'aria-label': 'Construction Type',
              }}
              disabled={!canEdit}
              fullWidth
            >
              <MenuItem value="">Choose a construction type</MenuItem>
              {(['Building', 'Residential', 'Highway', 'Heavy'] as DavisBaconConstructionType[]).map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
            </TextField>
          </ExternalField>
        <Box sx={contractDetailsFullWidthSx} data-tour-id="price-labor-plan"><ApproximateCostSettings
          value={draft.approximate_cost_plan_settings}
          roofInsulationSowEvidenceQuote={prefill?.roof_insulation_sow_evidence_quote}
          roofInsulationSowMinimumR={prefill?.roof_insulation_sow_minimum_r}
          recipes={capabilities?.approximate_cost_plan?.supported_recipes ?? []}
          componentReviewCandidates={load?.component_confirmation_queue ?? []}
          materialOccurrenceProposals={prefill?.material_occurrence_proposals ?? []}
          assemblyOccurrenceProposals={prefill?.assembly_occurrence_proposals ?? []}
          componentTakeoffProposals={prefill?.component_takeoff_proposals ?? []}
          flowId={flowId} savedDraftRevision={draft.draft_revision}
          unresolvedScopeCandidates={load?.component_scope_review_queue ?? []}
          activeCatalogMaterialComponents={load?.active_catalog_material_components ?? []}
          selectableWdClassifications={load?.selectable_wd_classifications ?? []}
          stale={prefill?.status === 'stale'}
          disabled={!canEdit || sourceSearchBusy || preparingPrefill}
          onIncompleteComponentChange={onIncompleteComponentChange}
          onChange={onSettingsChange} laborPlan={laborPlan} /></Box>
        <Box sx={contractDetailsFullWidthSx}><TimingRequirements
          items={draft.timing_requirements} canEdit={canEdit && !sourceSearchBusy}
          sourceStatus={prefill?.timing_source_status}
          stale={(prefill?.status === 'stale' || !prefill)
            && Boolean(draft.timing_requirements?.some(item => item.source_id.trim() && item.evidence_quote.trim()))}
          onChange={items => editDraft(current => ({ ...current, timing_requirements: items }))} /></Box>
        {blockers.length > 0 && (
          <Alert severity="warning" aria-label="Calculation blockers">
            <Typography fontWeight={700}>Needed before calculating prices:</Typography>
            <Box component="ul" sx={{ mb: 0 }}>{blockers.map((blocker, index) => <li key={`${blockerText(blocker)}-${index}`}>{blockerText(blocker)}</li>)}</Box>
          </Alert>
        )}
        </Box>
        </Box>
);
